import Image from 'next/image';
import Link from 'next/link';
import type { NextRouter } from 'next/router';
import { useRef } from 'react';

import type { MenuHierarchyRecordModelType } from '@/models/api';
import type { MenuPath } from '@/shared/types';

interface IPageLinksMobile {
  categoryMenuHierarchy: MenuHierarchyRecordModelType;
  currentPath: string;
  menuPath: MenuPath;
  router: NextRouter;
}

const SubCategoryTile: React.FC<{
  link: MenuHierarchyRecordModelType;
  isActive: boolean;
  name?: string;
}> = ({ link, isActive, name }) => {
  const baseStyle = 'flex h-[56px] flex-col items-center rounded-md p-2';
  const activeStyle = 'bg-black text-white';
  const inactiveStyle = 'bg-neutrals-liteGrey text-black';
  return (
    <li>
      <div
        className={`${baseStyle} ${isActive ? activeStyle : inactiveStyle} `}
      >
        <span className="mb-2 font-interMedium text-xs">
          {name || link.name}
        </span>
        {link.image?.url && (
          <div>
            <Image
              src={link.image?.url || ''}
              alt={link.name || ''}
              width={60}
              height={40}
            />
          </div>
        )}
      </div>
    </li>
  );
};

const RangeTile: React.FC<{
  link: MenuHierarchyRecordModelType;
  isActive: boolean;
  name?: string;
}> = ({ link, isActive, name }) => {
  const baseStyle =
    'text-black h-full flex items-center justify-center p-2 w-full ';
  const activeStyle = 'border-b-2 border-black font-interBold';
  const inactiveStyle = '';
  return (
    <li className={`${baseStyle} ${isActive ? activeStyle : inactiveStyle}`}>
      {name || link.name}
    </li>
  );
};

const sortBySlug = (
  a: MenuHierarchyRecordModelType,
  b: MenuHierarchyRecordModelType,
  match: string
) => {
  if (a.slug === match) return -1;
  if (b.slug === match) return 1;
  return 0;
};

const PageLinksMobile: React.FC<IPageLinksMobile> = ({
  categoryMenuHierarchy,
  currentPath,
  menuPath,
  router,
}) => {
  const { code, mode } = router.query;
  const { category, subcategory, range } = menuPath;
  const hasChildren = categoryMenuHierarchy?.childrenCategories?.length > 0;
  const subCategoryContainer = useRef(null);
  const rangeContainer = useRef(null);

  if (!hasChildren) return null;

  const subCategoryMenuHierarchy =
    categoryMenuHierarchy.childrenCategories.find(
      (child: MenuHierarchyRecordModelType) => child.slug === subcategory
    );

  const subCategoryHasChildren =
    subCategoryMenuHierarchy &&
    subCategoryMenuHierarchy?.childrenCategories?.length > 0;

  const subCategories = [
    categoryMenuHierarchy,
    ...categoryMenuHierarchy.childrenCategories,
  ] as MenuHierarchyRecordModelType[];

  let ranges: MenuHierarchyRecordModelType[] = [];

  if (subCategoryMenuHierarchy && subCategoryMenuHierarchy.childrenCategories) {
    const updatedSubcategory = {
      ...subCategoryMenuHierarchy,
      category: 'subcategory',
    };
    ranges = [
      updatedSubcategory,
      ...subCategoryMenuHierarchy.childrenCategories,
    ];
  }

  const subCategoryMatch = subcategory || category;
  const sortedChildrenCategories = subCategories
    .slice()
    .sort((a, b) => sortBySlug(a, b, subCategoryMatch));

  const rangeMatch = (range || subcategory) as string;
  const sortedChildrenRanges = ranges
    .slice()
    .sort((a, b) => sortBySlug(a, b, rangeMatch));

  const resetSubcategoryScroll = () => {
    if (subCategoryContainer && subCategoryContainer.current) {
      (subCategoryContainer.current as HTMLElement).scrollLeft = 0;
    }
  };

  const resetRangeScroll = () => {
    if (rangeContainer && rangeContainer.current) {
      (rangeContainer.current as HTMLElement).scrollLeft = 0;
    }
  };

  return (
    <div className="h-full overscroll-none">
      <ul
        ref={subCategoryContainer}
        className={`hide-scroll flex	 space-x-3 overflow-y-hidden overflow-x-scroll overscroll-none ${
          subCategoryHasChildren ? 'pb-3' : 'pb-6'
        }`}
      >
        {hasChildren &&
          sortedChildrenCategories.map((link) => {
            const isParent = link?.category === 'category';

            if (isParent) {
              return (
                <Link
                  href={{
                    pathname: `/${currentPath}/${link.slug}`,
                    query: {
                      ...(mode && { mode }),
                      ...(code && { code }),
                    },
                  }}
                  onClick={() => resetSubcategoryScroll()}
                  key={link.id}
                  className="w-[40%] max-w-[140px] flex-none"
                >
                  <SubCategoryTile
                    link={link}
                    isActive={!subcategory}
                    name={`All ${link.name}`}
                  />
                </Link>
              );
            }
            return (
              <Link
                key={link.id}
                onClick={() => resetSubcategoryScroll()}
                href={{
                  pathname: `/${currentPath}/${category}/${link.slug}`,
                  query: {
                    ...(mode && { mode }),
                    ...(code && { code }),
                  },
                }}
                className="w-[40%] max-w-[140px] flex-none"
              >
                <SubCategoryTile
                  link={link}
                  isActive={subcategory === link.slug}
                />
              </Link>
            );
          })}
      </ul>
      {subCategoryHasChildren && (
        <ul
          ref={rangeContainer}
          className="hide-scroll flex space-x-3 overflow-y-hidden overflow-x-scroll overscroll-none border-b border-grey"
        >
          {sortedChildrenRanges.map((link) => {
            const isParent = link?.category === 'subcategory';

            if (isParent) {
              return (
                <Link
                  key={link.id}
                  className="shrink-0 grow"
                  href={{
                    pathname: `/${currentPath}/${category}/${subcategory}`,
                    query: {
                      ...(mode && { mode }),
                      ...(code && { code }),
                    },
                  }}
                  onClick={() => resetRangeScroll()}
                >
                  <RangeTile
                    link={link}
                    isActive={!range}
                    name={`All ${link.name}`}
                  />
                </Link>
              );
            }

            return (
              <Link
                key={link.id}
                className="shrink-0 grow"
                href={{
                  pathname: `/${currentPath}/${category}/${subcategory}/${link.slug}`,
                  query: {
                    ...(mode && { mode }),
                    ...(code && { code }),
                  },
                }}
                onClick={() => resetRangeScroll()}
              >
                <RangeTile link={link} isActive={range === link.slug} />
              </Link>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PageLinksMobile;
